// Source: https://github.com/amirhhashemi/tiptap-text-direction/blob/master/src/index.ts
// Copied to remove the auto detection of text direction
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const validDirections = ['ltr', 'rtl', 'auto'] as const;

type Direction = (typeof validDirections)[number];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    textDirection: {
      /**
       * Set the text direction attribute
       */
      setTextDirection: (direction: Direction) => ReturnType;
    };
  }
}

export interface TextDirectionOptions {
  types: string[];
  defaultDirection: Direction | null;
}

const TextDirection = Extension.create<TextDirectionOptions>({
  name: 'textDirection',

  addOptions() {
    return {
      types: ['paragraph', 'heading'],
      defaultDirection: 'auto',
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          dir: {
            default: null,
            rendered: true,
            parseHTML: element => element.dir || this.options.defaultDirection,
            renderHTML: attributes => {
              return { dir: attributes.dir };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setTextDirection:
        (direction: Direction) =>
        ({ commands }) => {
          if (!validDirections.includes(direction)) {
            return false;
          }

          this.options.defaultDirection = direction;

          return this.options.types.every(type =>
            commands.updateAttributes(type, { dir: direction }),
          );
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('textDirection'),
        appendTransaction: (transactions, _oldState, newState) => {
          const docChanges = transactions.some(transaction => transaction.docChanged);
          if (!docChanges) {
            return;
          }

          let modified = false;
          const tr = newState.tr;

          newState.doc.descendants((node, pos) => {
            if (this.options.types.includes(node.type.name)) {
              if (node.attrs.dir !== null && node.textContent.length > 0) {
                return;
              }

              const marks = tr.storedMarks || [];
              tr.setNodeAttribute(pos, 'dir', this.options.defaultDirection);
              // `tr.setNodeAttribute` resets the stored marks so we'll restore them
              for (const mark of marks) {
                tr.addStoredMark(mark);
              }
              modified = true;
            }
          });

          return modified ? tr : null;
        },
      }),
    ];
  },
});

export default TextDirection;
